import { z } from 'zod';
import { PlaygroundEngine } from './playgrounds.js';
import { BuildSource, Platform, Ulid } from './common.js';
/** BoardFlight's product events. Game analytics and tester feedback stay in D1. */
export const PRODUCT_PATHS = [
    '/', '/players', '/creators', '/downloads', '/install', '/get', '/guides', '/guides/:guide', '/start', '/terms', '/privacy',
    '/signin', '/signup', '/welcome', '/account', '/link', '/forgot', '/reset/:token',
    '/verify/:token', '/invite/:token', '/redeem', '/redeem/:code', '/say/:key/:build',
    '/auth/:provider', '/playground/oauth/callback', '/g/:code', '/library', '/library/search',
    '/library/mine', '/library/boards', '/library/welcome', '/library/account', '/library/game/:appId',
    '/u/me', '/u/:username', '/keep/:code', '/teams/new', '/teams/:teamId',
    '/teams/:teamId/games', '/teams/:teamId/games/:gameId',
    '/teams/:teamId/games/:gameId/playground', '/games/:gameId',
    '/games/:gameId/playground', '/unknown',
];
export const PRODUCT_SECTIONS = [
    'builds', 'channels', 'settings', 'listing', 'testers', 'codes', 'activity', 'analytics',
    'feedback', 'access', 'general', 'developer', 'actions', 'members', 'tokens', 'mcp', 'profile', 'sessions', 'unknown',
];
/**
 * What a `download_clicked` may name. `installer` and `board_client` are the retired Board client's
 * and no link carries either any more; they stay because events already recorded with them have to
 * go on reading. `desktop_app` is the desktop app's installer, on `/get`.
 */
export const PRODUCT_DOWNLOADS = ['installer', 'board_client', 'unity_plugin', 'cli_script', 'desktop_app'];
/**
 * Which host a browser event was sent from — the site is one bundle on two
 * (`docs/library-first-plan.md` §2) — and `other` for any copy of it served anywhere else. A closed
 * list rather than the page's own hostname, for the reason every property here is closed.
 */
export const PRODUCT_HOSTS = ['boardflight.app', 'create.boardflight.app', 'other'];
export function productHost(hostname) {
    return hostname !== 'other' && PRODUCT_HOSTS.includes(hostname) ? hostname : 'other';
}
/** What drew a browser event: a browser tab, or the desktop app's window, which loads the same site. */
export const PRODUCT_SHELLS = ['web', 'desktop'];
/** What every browser event carries beside its own properties. The API's events carry neither. */
export const ProductBrowserContext = z.object({ host: z.enum(PRODUCT_HOSTS), shell: z.enum(PRODUCT_SHELLS) });
const game = { team_id: Ulid, app_id: Ulid };
const playground = { ...game, playground_id: Ulid, engine: PlaygroundEngine };
const build = { ...game, build_id: Ulid, channel_id: Ulid };
/** Closed properties as well as closed names: no free text, URLs, names, plans or credentials. */
export const PRODUCT_EVENTS = {
    $pageview: z.object({
        path: z.enum(PRODUCT_PATHS), signed_in: z.boolean(),
        section: z.enum(PRODUCT_SECTIONS).optional(),
        referrer: z.enum(['direct', 'search', 'social', 'community', 'internal', 'other']),
    }),
    $identify: z.object({ $anon_distinct_id: z.string().regex(/^anon:[0-9a-f-]{36}$/) }),
    download_clicked: z.object({ target: z.enum(PRODUCT_DOWNLOADS) }),
    account_created: z.object({ method: z.enum(['password', 'google', 'discord']) }),
    account_signed_in: z.object({}),
    team_created: z.object({ team_id: Ulid }),
    creator_onboarding_completed: z.object({ answered: z.boolean() }),
    game_created: z.object({ ...game, platform: Platform, origin: z.enum(['new', 'clone']) }),
    playground_created: z.object(playground),
    playground_agent_changed: z.object({ ...playground, agent: z.enum(['codex', 'external', 'claudecode']) }),
    playground_launched: z.object({ ...playground, session_id: Ulid, agent: z.enum(['codex', 'external', 'claudecode']) }),
    playground_launch_failed: z.object({ ...playground, agent: z.enum(['codex', 'external', 'claudecode']) }),
    playground_stopped: z.object({ ...playground, reason: z.enum(['stopped', 'page_closed']) }),
    build_upload_created: z.object({ ...build, build_source: BuildSource }),
    build_finalized: z.object({ ...build, status: z.enum(['ready', 'patching']) }),
    build_promoted: z.object({ ...build, copied: z.boolean() }),
    build_download_authorized: z.object({ ...build, delivery: z.enum(['artifact', 'url']) }),
    store_submission_requested: z.object(game),
};
export const ProductSessionId = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/);
const POSTHOG_PROXY = 'https://n.boardaux.com';
/** Cloud ingestion and our approved managed proxy; other URLs must never receive product data. */
export function postHogConfig(token, host) {
    const cleanToken = token?.trim();
    const cleanHost = host?.trim().replace(/\/+$/, '') || 'https://us.i.posthog.com';
    if (!cleanToken || !['https://us.i.posthog.com', 'https://eu.i.posthog.com', POSTHOG_PROXY].includes(cleanHost))
        return null;
    return { token: cleanToken, host: cleanHost };
}
/**
 * The single filtering boundary for browser and API events; unexpected properties are stripped.
 *
 * `browser` is the page's host and shell, for a `web` event only. A page view's URL names the host
 * it was drawn on, so `/` on the library and `/` on the creator dashboard are two pages; one from a
 * host that is neither names no URL at all rather than borrow the library's.
 */
export function productPayload(event, distinctId, props, source, insertId = crypto.randomUUID(), now = Date.now(), sessionId, browser) {
    if (!/^(user:[0-9A-HJKMNP-TV-Z]{26}|token:[0-9A-HJKMNP-TV-Z]{26}|anon:[0-9a-f-]{36})$/.test(distinctId))
        return null;
    const parsed = PRODUCT_EVENTS[event].safeParse(props);
    if (!parsed.success)
        return null;
    const properties = {
        ...parsed.data, source, schema_version: 1, $insert_id: insertId,
        $geoip_disable: true, $ip: null,
        // Opaque account IDs only. Browsing needs no person profile.
        $process_person_profile: distinctId.startsWith('user:'),
    };
    if (sessionId && ProductSessionId.safeParse(sessionId).success)
        properties.$session_id = sessionId;
    const context = source === 'web' && browser ? ProductBrowserContext.safeParse(browser) : null;
    if (context?.success) {
        properties.$host = context.data.host;
        properties.shell = context.data.shell;
    }
    if (event === '$pageview' && 'path' in parsed.data) {
        // Without a context — a caller from before there were two hosts — the library's, as it always was.
        const host = context?.success ? context.data.host : 'boardflight.app';
        if (host !== 'other')
            properties.$current_url = `https://${host}${parsed.data.path}`;
        properties.$pathname = parsed.data.path;
    }
    return { event, distinct_id: distinctId, timestamp: new Date(now).toISOString(), properties };
}
/** No SDK defaults, cookies, autocapture, replay, flags or exception capture. */
export async function sendProductPayload(config, payload, keepalive = false) {
    // PostHog's proxy health check uses this property to recognize proxied events.
    const properties = config.host === POSTHOG_PROXY
        ? { ...payload.properties, $lib_custom_api_host: POSTHOG_PROXY }
        : payload.properties;
    const response = await fetch(`${config.host}/i/v0/e/`, {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ api_key: config.token, ...payload, properties }),
        signal: AbortSignal.timeout(2500), keepalive, credentials: 'omit',
    });
    // No response body or exception text ever gets logged (both can contain credentials).
    await response.body?.cancel();
    if (!response.ok)
        throw new Error('Product analytics delivery failed.');
}
//# sourceMappingURL=product-analytics.js.map